// Speech Recognition Service
// Uses Web Speech API for real-time transcription + AI Translation

import { getLanguageById } from './languageConfig';

// API base URL - adjust based on environment
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * Translate text using backend translation API
 * @param {string} text - Text to translate
 * @param {string} sourceLang - Source language code
 * @param {string} targetLang - Target language code
 * @returns {Promise<string>} Translated text
 */
export async function translateText(text, sourceLang, targetLang) {
  // Same language - no translation needed
  if (sourceLang === targetLang || !text.trim()) {
    return text;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/translate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        text,
        sourceLang,
        targetLang,
      }),
    });

    if (!response.ok) {
      throw new Error(`Translation failed: ${response.status}`);
    }

    const data = await response.json();
    return data.translation || text;
  } catch (error) {
    console.error('Translation error:', error);
    // Return original text on error
    return text;
  }
}

// Errors that are normal/expected and should not be shown to user
const IGNORED_ERRORS = ['no-speech', 'aborted', 'network'];

// Errors that need user action
const CRITICAL_ERRORS = {
  'not-allowed': 'Microphone access denied. Please allow microphone access in your browser settings.',
  'audio-capture': 'No microphone found. Please connect a microphone.',
  'service-not-allowed': 'Speech recognition service is not allowed. Please check your browser settings.'
};

/**
 * Keep a microphone stream active to help Web Speech API work alongside VideoSDK
 * This prevents the browser from releasing the mic between recognition sessions
 */
let keepAliveStream = null;

async function ensureMicrophoneAccess() {
  try {
    // If we already have a stream, check if it's still active
    if (keepAliveStream && keepAliveStream.active) {
      console.log('[Captions] Microphone stream already active');
      return true;
    }

    console.log('[Captions] Requesting microphone access...');
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      }
    });

    // Keep this stream alive - it helps Web Speech API work when VideoSDK is also using mic
    keepAliveStream = stream;
    console.log('[Captions] ✓ Microphone access granted, stream active');
    return true;
  } catch (error) {
    console.error('[Captions] Microphone access denied:', error);
    return false;
  }
}

function releaseMicrophoneStream() {
  if (keepAliveStream) {
    keepAliveStream.getTracks().forEach(track => track.stop());
    keepAliveStream = null;
    console.log('[Captions] Microphone stream released');
  }
}

/**
 * Create a speech recognition controller
 */
export function createSpeechRecognition(languageCode, onResult, onError) {
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

  if (!SpeechRecognition) {
    return {
      isSupported: false,
      start: () => onError?.('Speech recognition not supported in this browser'),
      stop: () => {},
    };
  }

  const language = getLanguageById(languageCode);
  let recognition = null;
  let currentLang = language?.code || 'en-US';
  let isRunning = false;
  let lastErrorTime = 0;
  let errorCount = 0;
  let restartCount = 0;
  let hasReceivedAudio = false;
  let startTime = 0;

  const setupRecognition = () => {
    // Abort any existing recognition first
    if (recognition) {
      try {
        recognition.abort();
      } catch (e) {
        // Ignore
      }
    }

    recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = currentLang;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      console.log('[Captions] ✓ Recognition started, language:', currentLang);
      startTime = Date.now();
    };

    recognition.onaudiostart = () => {
      console.log('[Captions] ✓ Microphone active - audio stream started');
      hasReceivedAudio = true;
      restartCount = 0; // Reset restart count only when we actually get audio
    };

    recognition.onaudioend = () => {
      console.log('[Captions] Audio stream ended');
    };

    recognition.onspeechstart = () => {
      console.log('[Captions] ✓ Speech detected!');
    };

    recognition.onspeechend = () => {
      console.log('[Captions] Speech ended');
    };

    recognition.onsoundstart = () => {
      console.log('[Captions] ✓ Sound detected');
    };

    recognition.onsoundend = () => {
      console.log('[Captions] Sound ended');
    };

    recognition.onresult = (event) => {
      const resultIndex = event.resultIndex;
      const result = event.results[resultIndex];

      if (result) {
        console.log('[Captions] ✓ Result:', result[0].transcript, 'Final:', result.isFinal);
        errorCount = 0;
        restartCount = 0;
        onResult({
          text: result[0].transcript,
          isFinal: result.isFinal,
          confidence: result[0].confidence,
          timestamp: Date.now()
        });
      }
    };

    recognition.onerror = (event) => {
      const error = event.error;

      // Log all errors for debugging
      console.log('[Captions] Error event:', error);

      // Ignore expected errors
      if (IGNORED_ERRORS.includes(error)) {
        return;
      }

      // Debounce errors
      const now = Date.now();
      if (now - lastErrorTime < 3000) {
        errorCount++;
        if (errorCount > 5) {
          isRunning = false;
          onError?.('Speech recognition encountered repeated errors. Please try again.');
        }
        return;
      }
      lastErrorTime = now;
      errorCount = 1;

      if (CRITICAL_ERRORS[error]) {
        onError?.(CRITICAL_ERRORS[error]);
        isRunning = false;
      }
    };

    recognition.onend = () => {
      const sessionDuration = Date.now() - startTime;
      console.log('[Captions] Session ended after', sessionDuration, 'ms, isRunning:', isRunning, 'restartCount:', restartCount, 'hasReceivedAudio:', hasReceivedAudio);

      if (isRunning) {
        restartCount++;

        // If session ended very quickly without receiving audio, there might be an issue
        if (sessionDuration < 500 && !hasReceivedAudio) {
          console.log('[Captions] Session ended too quickly without audio - possible mic issue');
        }

        // Progressive backoff: start at 200ms, increase up to 2000ms
        const delay = Math.min(200 + (restartCount * 100), 2000);

        console.log('[Captions] Will restart in', delay, 'ms');

        setTimeout(() => {
          if (isRunning) {
            hasReceivedAudio = false;
            try {
              recognition.start();
            } catch (e) {
              console.log('[Captions] Restart error:', e.message, '- recreating recognition object');
              setupRecognition();
              try {
                recognition.start();
              } catch (e2) {
                console.warn('[Captions] Failed to restart after recreate:', e2.message);
                // If we keep failing, slow down even more
                if (restartCount > 20) {
                  console.warn('[Captions] Too many restart failures, stopping');
                  isRunning = false;
                  onError?.('Speech recognition keeps failing. Please refresh the page and try again.');
                }
              }
            }
          }
        }, delay);
      }
    };
  };

  return {
    isSupported: true,
    start: async () => {
      console.log('[Captions] Starting speech recognition...');

      // Ensure we have microphone access first
      const hasAccess = await ensureMicrophoneAccess();
      if (!hasAccess) {
        onError?.('Microphone access denied. Please allow microphone access for captions.');
        return;
      }

      // Small delay to ensure mic stream is established
      await new Promise(resolve => setTimeout(resolve, 100));

      console.log('[Captions] Starting for language:', currentLang);
      isRunning = true;
      errorCount = 0;
      restartCount = 0;
      hasReceivedAudio = false;

      setupRecognition();

      try {
        recognition.start();
        console.log('[Captions] Recognition start() called');
      } catch (e) {
        console.error('[Captions] Failed to start:', e.message);

        // If failed due to already started, try to stop and restart
        if (e.message.includes('already started')) {
          try {
            recognition.abort();
            await new Promise(resolve => setTimeout(resolve, 200));
            if (isRunning) {
              setupRecognition();
              recognition.start();
            }
          } catch (e2) {
            onError?.('Failed to start speech recognition: ' + e.message);
          }
        } else {
          onError?.('Failed to start speech recognition: ' + e.message);
        }
      }
    },
    stop: () => {
      console.log('[Captions] Stopping...');
      isRunning = false;
      try {
        if (recognition) {
          recognition.abort();
        }
      } catch (e) {
        // Already stopped
      }
      // Release the microphone stream when stopping
      releaseMicrophoneStream();
    },
    setLanguage: (langCode) => {
      const lang = getLanguageById(langCode);
      currentLang = lang?.code || 'en-US';
      console.log('[Captions] Language changed to:', currentLang);
      if (isRunning && recognition) {
        try {
          recognition.stop();
        } catch (e) {}
        setupRecognition();
        try {
          recognition.start();
        } catch (e) {}
      }
    }
  };
}

/**
 * Create a caption session with speech recognition + translation
 */
export function createCaptionSession({
  spokenLanguage,
  captionLanguage,
  onCaption,
  onError
}) {
  let lastFinalText = '';
  let currentSpokenLanguage = spokenLanguage;
  let currentCaptionLanguage = captionLanguage;

  const handleSpeechResult = async (result) => {
    const needsTranslation = currentSpokenLanguage !== currentCaptionLanguage;

    // For interim results - show immediately
    if (!result.isFinal) {
      onCaption({
        text: result.text,
        isFinal: false,
        isTranslating: false,
        timestamp: result.timestamp
      });
      return;
    }

    // Skip empty or duplicate results
    if (!result.text.trim() || result.text === lastFinalText) return;
    lastFinalText = result.text;

    // If same language, no translation needed
    if (!needsTranslation) {
      onCaption({
        text: result.text,
        isFinal: true,
        isTranslating: false,
        timestamp: result.timestamp
      });
      return;
    }

    // Show original text first, then translate in background
    onCaption({
      text: result.text,
      originalText: result.text,
      isFinal: true,
      isTranslating: true,
      timestamp: result.timestamp
    });

    // Translate asynchronously (don't block next captions)
    translateText(result.text, currentSpokenLanguage, currentCaptionLanguage)
      .then(translated => {
        if (translated !== result.text) {
          onCaption({
            text: translated,
            originalText: result.text,
            isFinal: true,
            isTranslating: false,
            wasTranslated: true,
            timestamp: result.timestamp
          });
        }
      })
      .catch(error => {
        console.error('Translation failed:', error);
      });
  };

  const recognition = createSpeechRecognition(
    currentSpokenLanguage,
    handleSpeechResult,
    onError
  );

  return {
    isSupported: recognition.isSupported,
    start: recognition.start,
    stop: recognition.stop,
    updateLanguages: (spoken, caption) => {
      currentSpokenLanguage = spoken;
      currentCaptionLanguage = caption;
      recognition.setLanguage?.(spoken);
    }
  };
}
